// Unified dedicated page for any active side game on a standalone round.
//
// Route: /game/:gameId
// Chip strip on the scorecard routes here for every game format. Header
// is common (Back + Edit + End). Content fans out on game.format:
//   * shuffle_card → <ShuffleCard variant="full" />
//   * wolf         → <WolfCard />
//   * skins        → <SkinsCard />
//   * nassau, stableford, simple, match_1v1 → minimal placeholder that
//     lists participants + current standings via server compute (or a
//     "Game in progress" holder if no card component exists yet).
//
// Edit affordance (mid-game tweak of format-specific config):
//   * shuffle_card → mode radios (server locks after any card is drawn)
//   * wolf         → rotation reorder (server locks after any decision)
//   * everything else → unit_value input
//
// End action → confirms then calls end_game and navigates back.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import ShuffleCard from '../components/ShuffleCard'
import WolfCard from '../components/WolfCard'
import SkinsCard from '../components/SkinsCard'
import NassauCard from '../components/NassauCard'
import SimpleGameCard from '../components/SimpleGameCard'
import StablefordCard from '../components/StablefordCard'
import MatchCard from '../components/MatchCard'
import VegasCard from '../components/VegasCard'

function safeParse(raw, fallback) {
  try { const p = JSON.parse(raw); return p ?? fallback } catch { return fallback }
}

async function callStandalone(action, payload) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Please sign in again.')
  const res = await fetch('/.netlify/functions/standalone-game', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ action, ...payload }),
  })
  const json = await res.json()
  if (!res.ok) {
    const err = new Error(json.error || `${action} failed`)
    err.code = json.code
    throw err
  }
  return json.data
}

// Scorecard's per-round progress bucket. GamePage polls this so games
// that need the current hole (Shuffle picker, Wolf active-hole banner)
// stay in step with the scorekeeper's actual hole even when opened
// from a different route.
function scorecardKeyFor(playerId, roundId) {
  return `standalone_round_${playerId || 'anon'}_${roundId || 'anon'}`
}

const FORMAT_TITLES = {
  shuffle_card: 'Shuffle Card',
  wolf:         'Wolf',
  skins:        'Skins',
  nassau:       'Nassau',
  stableford:   'Stableford',
  simple:       'Simple',
  match_1v1:    'Match Play',
  vegas:        'Vegas',
}

export default function GamePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const { player } = useAuth()

  // Accept the game_id either via path param (/game/:gameId) or query
  // string (?game=<id>) for back-compat with the previous /shuffle
  // scheme. Path param takes precedence when both present.
  const gameId = useMemo(() => {
    if (params.gameId) return params.gameId
    const p = new URLSearchParams(location.search)
    return p.get('game') || null
  }, [params.gameId, location.search])

  const [game, setGame] = useState(null)
  const [participants, setParticipants] = useState([])
  const [standaloneRoundId, setStandaloneRoundId] = useState(null)
  const [currentHole, setCurrentHole] = useState(1)
  const [holePar, setHolePar] = useState(4)
  const [err, setErr] = useState(null)
  const [toast, setToast] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [busy, setBusy] = useState(false)
  const [confirmingEnd, setConfirmingEnd] = useState(false)

  // Discover the standalone round from localStorage AND poll it. The
  // scorecard writes `{currentHole}` to this key on every save + hole
  // navigation; polling every 1s keeps GamePage's currentHole within
  // one tick of the scorecard's actual hole.
  const syncFromLocalStorage = useCallback(() => {
    try {
      const raw = localStorage.getItem('active_round')
      const ar = safeParse(raw, null)
      if (ar?.id) setStandaloneRoundId(ar.id)
      if (ar?.id && player?.id) {
        const key = scorecardKeyFor(player.id, ar.id)
        const saved = safeParse(localStorage.getItem(key), null)
        const savedHole = Number(saved?.currentHole)
        if (Number.isFinite(savedHole) && savedHole >= 1) {
          setCurrentHole(prev => (prev === savedHole ? prev : savedHole))
          if (ar.course?.holes_data && Array.isArray(ar.course.holes_data)) {
            const h = ar.course.holes_data.find(x => x.hole_number === savedHole)
            if (h?.par) setHolePar(h.par)
          }
        }
      }
    } catch {}
  }, [player?.id])

  useEffect(() => {
    syncFromLocalStorage()
    const id = setInterval(syncFromLocalStorage, 1000)
    return () => clearInterval(id)
  }, [syncFromLocalStorage])

  // Fetch game details + participants via list_games. Runs whenever
  // gameId or standaloneRoundId lands, plus on-demand after an edit.
  const loadGame = useCallback(async () => {
    if (!gameId || !standaloneRoundId) return
    setLoading(true)
    try {
      const data = await callStandalone('list_games', { standalone_round_id: standaloneRoundId })
      const found = (data || []).find(g => g.id === gameId)
      if (!found) {
        setErr('Game not found on this round.')
      } else {
        setGame(found)
        setParticipants(found.participants || [])
        setErr(null)
      }
    } catch (e) {
      setErr(e.message)
    }
    setLoading(false)
  }, [gameId, standaloneRoundId])
  useEffect(() => { loadGame() }, [loadGame])

  const showToast = (msg) => {
    setToast(msg)
    setTimeout(() => setToast(null), 3500)
  }

  const saveConfig = async (patch) => {
    setBusy(true)
    try {
      const data = await callStandalone('update_game_config', {
        game_id: gameId,
        config: patch,
      })
      setGame(g => ({ ...(g || {}), config: data?.config || { ...(g?.config || {}), ...patch } }))
      showToast('Saved.')
    } catch (e) {
      showToast(e.message)
    }
    setBusy(false)
  }

  const endGame = async () => {
    setBusy(true)
    try {
      await callStandalone('end_game', { game_id: gameId })
      showToast('Game ended.')
      setTimeout(() => navigate(-1), 800)
    } catch (e) {
      showToast(e.message)
    }
    setBusy(false)
    setConfirmingEnd(false)
  }

  const format = game?.format || null
  const title = FORMAT_TITLES[format] || 'Side Game'

  return (
    <div className="page">
      <div className="container">
        <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            style={{
              width: 36, height: 36, borderRadius: 6,
              border: '1px solid var(--green-mid)',
              background: 'var(--green-deep)', color: 'var(--cream)',
              fontSize: '1.2rem', cursor: 'pointer',
            }}
          >
            ‹
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p className="eyebrow">Side Game</p>
            <h1 style={{ margin: 0 }}>{title}</h1>
          </div>
          {game && !game.ended_at && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => setEditMode(m => !m)}
                style={{
                  padding: '6px 12px', border: '1px solid var(--gold)',
                  background: 'transparent', color: 'var(--gold)',
                  borderRadius: 6, fontSize: '0.8rem', cursor: 'pointer',
                }}
              >
                {editMode ? 'Close' : 'Edit'}
              </button>
              <button
                onClick={() => setConfirmingEnd(true)}
                disabled={busy}
                style={{
                  padding: '6px 12px', border: '1px solid var(--gray-500)',
                  background: 'transparent', color: 'var(--gray-300)',
                  borderRadius: 6, fontSize: '0.8rem',
                  cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1,
                }}
              >
                End
              </button>
            </div>
          )}
          {game?.ended_at && (
            <span style={{
              padding: '4px 10px', borderRadius: 999,
              border: '1px solid var(--gray-500)', color: 'var(--gray-300)',
              fontSize: '0.7rem', textTransform: 'uppercase',
            }}>Ended</span>
          )}
        </div>

        {editMode && game && (
          <EditGamePanel
            game={game}
            participants={participants}
            busy={busy}
            onSave={saveConfig}
          />
        )}

        {confirmingEnd && (
          <div
            onClick={() => !busy && setConfirmingEnd(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 1200,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 16,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                maxWidth: 320, padding: 20, borderRadius: 10,
                background: 'var(--green-deep)',
                border: '1px solid var(--gold)',
              }}
            >
              <h3 style={{ margin: '0 0 8px', color: 'var(--gold)' }}>End {title}?</h3>
              <p style={{ fontSize: '0.85rem', color: 'var(--cream)', lineHeight: 1.4 }}>
                Final state stays visible on the round recap. No further edits or picks.
              </p>
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button
                  onClick={() => setConfirmingEnd(false)}
                  disabled={busy}
                  style={{
                    flex: 1, padding: '10px 12px', borderRadius: 6,
                    background: 'transparent', color: 'var(--cream)',
                    border: '1px solid var(--green-mid)', cursor: 'pointer',
                  }}
                >Cancel</button>
                <button
                  onClick={endGame}
                  disabled={busy}
                  style={{
                    flex: 1, padding: '10px 12px', borderRadius: 6,
                    background: 'var(--gold)', color: 'var(--green-deep)',
                    border: 'none', fontWeight: 700,
                    cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1,
                  }}
                >End Game</button>
              </div>
            </div>
          </div>
        )}

        {toast && (
          <div className="card card-sm" style={{ marginTop: 8, fontSize: '0.82rem' }}>
            {toast}
          </div>
        )}

        {loading && !game && (
          <p className="text-muted text-sm">Loading…</p>
        )}
        {err && !game && (
          <div className="card card-sm" style={{ marginTop: 12 }}>
            <p style={{ margin: 0, color: 'var(--red)' }}>{err}</p>
          </div>
        )}

        {game && (
          <div className="card" style={{ marginTop: 12 }}>
            {format === 'shuffle_card' && (
              <ShuffleCard
                game_id={gameId}
                viewerPlayerId={player?.id}
                viewerCurrentHole={currentHole}
                currentHolePar={holePar}
                variant="full"
              />
            )}
            {format === 'wolf' && (
              <WolfCard
                game_id={gameId}
                viewerPlayerId={player?.id}
                viewerCurrentHole={currentHole}
                autoRefreshMs={5000}
              />
            )}
            {format === 'skins' && (
              <SkinsCard
                game_id={gameId}
                autoRefreshMs={5000}
              />
            )}
            {format === 'nassau' && (
              <NassauCard game_id={gameId} autoRefreshMs={5000} />
            )}
            {format === 'stableford' && (
              <StablefordCard game_id={gameId} autoRefreshMs={5000} />
            )}
            {format === 'simple' && (
              <SimpleGameCard game_id={gameId} autoRefreshMs={5000} />
            )}
            {format === 'match_1v1' && (
              <MatchCard game_id={gameId} autoRefreshMs={5000} />
            )}
            {format === 'vegas' && (
              <VegasCard
                game_id={gameId}
                viewerPlayerId={player?.id}
                viewerCurrentHole={currentHole}
                autoRefreshMs={5000}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Edit form ──────────────────────────────────────────────────────────
function EditGamePanel({ game, participants, busy, onSave }) {
  const format = game.format
  const [config, setConfig] = useState(game.config || {})
  useEffect(() => { setConfig(game.config || {}) }, [game])

  // Server-side numeric value the input mirrors. Local string state
  // below owns what the user sees so the field can be temporarily
  // empty during edits (Jacob 2026-07-14: "can't remove the 0 that
  // appears after removing 1, it wants to put 02 instead of just 2").
  const currentUnit = format === 'nassau'
    ? (config?.bet_units?.overall ?? 0)
    : format === 'match_1v1'
      ? (config?.bet_units ?? 0)
      : (config?.unit_value ?? 0)
  const [unitText, setUnitText] = useState(String(currentUnit))
  useEffect(() => { setUnitText(String(currentUnit)) }, [currentUnit])

  // F-101-T1 review fix: age gate on the edit path — the same 18+ ack
  // checkbox the StandaloneGamesPanel EditGameForm surfaces. Without
  // this, a $0→$5 unit bump on a stub-inclusive game hit an un-
  // satisfiable server rejection here (the server correctly rejects
  // with stub_age_ack_required, but this page had no checkbox for the
  // user to acknowledge). Mirror the create-form logic exactly —
  // require the ack only on wager-flipping edits (wager→wager stays
  // quiet per acceptance 6e), and forward stub_age_ack: true so the
  // server rewrites it to the audit-trail object.
  const wagerless = format === 'shuffle_card'
  const priorUnit = format === 'nassau'
    ? (game?.config?.bet_units?.overall ?? 0)
    : format === 'match_1v1'
      ? (game?.config?.bet_units ?? 0)
      : (game?.config?.unit_value ?? 0)
  const parsedUnit = Number(unitText)
  const editedUnit = Number.isFinite(parsedUnit) && parsedUnit >= 0 ? parsedUnit : 0
  const wasWager = !wagerless && Number(priorUnit) > 0
  const nowWager = !wagerless && editedUnit > 0
  const flippedToWager = !wasWager && nowWager
  const activeParticipantCount = Array.isArray(participants)
    ? participants.filter(p => !p.left_at).length
    : 0
  const stubAckRequired = flippedToWager && activeParticipantCount > 0
  const [stubAgeAck, setStubAgeAck] = useState(false)

  return (
    <div className="card card-sm" style={{ marginTop: 8 }}>
      {format === 'shuffle_card' && (
        <>
          <p style={{ fontSize: '0.78rem', color: 'var(--gray-300)', margin: '0 0 8px' }}>
            Mode (locked once cards have been drawn):
          </p>
          <div style={{ display: 'grid', gap: 4, marginBottom: 12 }}>
            {[
              { v: 'straight_up', label: 'Straight Up', desc: '1 card per group per hole' },
              { v: 'double_down', label: 'Double Down', desc: '2 cards per group per hole; effects stack' },
              { v: 'duel',        label: 'Duel',        desc: '2 sides, shared deck' },
            ].map(m => {
              const active = (config?.shuffle_mode || 'straight_up') === m.v
              return (
                <label
                  key={m.v}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 6,
                    padding: 8, borderRadius: 6,
                    background: active ? 'rgba(212,185,122,0.12)' : 'transparent',
                    border: `1px solid ${active ? 'var(--gold)' : 'var(--green-mid)'}`,
                    cursor: busy ? 'not-allowed' : 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    checked={active}
                    onChange={() => !busy && onSave({ shuffle_mode: m.v })}
                    disabled={busy}
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>{m.label}</span>
                    <span style={{ display: 'block', color: 'var(--gray-400)', fontSize: '0.72rem' }}>{m.desc}</span>
                  </span>
                </label>
              )
            })}
          </div>
        </>
      )}

      {format === 'wolf' && Array.isArray(config?.rotation_order) && (
        <>
          <p style={{ fontSize: '0.78rem', color: 'var(--gray-300)', margin: '0 0 8px' }}>
            Rotation order (locked once a Wolf decision has been made):
          </p>
          <div style={{ display: 'grid', gap: 4, marginBottom: 12 }}>
            {config.rotation_order.map((pid, i) => {
              const name = participants.find(p => p.player_id === pid)?.name || pid.slice(0, 6)
              return (
                <div key={pid} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 10px', borderRadius: 4,
                  background: 'var(--green-dark)', border: '1px solid var(--green-mid)',
                  fontSize: '0.85rem',
                }}>
                  <span style={{ color: 'var(--gray-400)', fontSize: '0.72rem' }}>{i + 1}.</span>
                  <span style={{ flex: 1 }}>{name}</span>
                  <button
                    disabled={i === 0 || busy}
                    onClick={() => {
                      const next = [...config.rotation_order]
                      ;[next[i - 1], next[i]] = [next[i], next[i - 1]]
                      onSave({ rotation_order: next })
                    }}
                    style={miniStyle}
                  >↑</button>
                  <button
                    disabled={i === config.rotation_order.length - 1 || busy}
                    onClick={() => {
                      const next = [...config.rotation_order]
                      ;[next[i], next[i + 1]] = [next[i + 1], next[i]]
                      onSave({ rotation_order: next })
                    }}
                    style={miniStyle}
                  >↓</button>
                </div>
              )
            })}
          </div>
        </>
      )}

      {format === 'vegas' && (
        <label style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
          fontSize: '0.82rem', cursor: busy ? 'not-allowed' : 'pointer',
        }}>
          <input
            type="checkbox"
            checked={config?.flip_enabled !== false}
            disabled={busy}
            onChange={(e) => onSave({ flip_enabled: e.target.checked })}
          />
          <span>
            Flip on natural birdie
            <span style={{ display: 'block', color: 'var(--gray-400)', fontSize: '0.72rem' }}>
              Opponent's natural birdie flips your number high; a natural eagle doubles the hole.
            </span>
          </span>
        </label>
      )}

      {format !== 'shuffle_card' && (
        <>
          <p style={{ fontSize: '0.78rem', color: 'var(--gray-300)', margin: '0 0 6px' }}>
            Unit value:
          </p>
          <div style={{ display: 'flex', gap: 6 }}>
            {/* Local string state so the field can be empty during
                edits — number state would coerce back to 0 and force
                "02"-style leading zeros. Parse only on Save. */}
            <input
              type="text" inputMode="decimal"
              value={unitText}
              onChange={e => setUnitText(e.target.value)}
              onBlur={() => {
                // Snap back to the last-known good value if the user
                // leaves the field blank.
                if (unitText.trim() === '') setUnitText(String(currentUnit))
              }}
              style={{
                width: 100, padding: 8, borderRadius: 4,
                background: 'var(--green-deep)', color: 'var(--cream)',
                border: '1px solid var(--green-mid)', fontSize: '0.9rem',
              }}
            />
            <button
              onClick={() => {
                const parsed = Number(unitText)
                const safe = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
                // F-101-T1 review fix: forward the ack when the edit
                // flips bragging→wager AND the user ticked the box.
                // Server rewrites `true` into the audit-trail object;
                // matches the create-path shape at
                // StandaloneGamesPanel.jsx:1352 so both paths share the
                // scrub logic.
                const patch = {}
                if (format === 'nassau') patch.bet_units = { front: safe, back: safe, overall: safe }
                else if (format === 'match_1v1') patch.bet_units = safe
                else patch.unit_value = safe
                if (stubAckRequired && stubAgeAck) patch.stub_age_ack = true
                onSave(patch)
              }}
              disabled={busy || (stubAckRequired && !stubAgeAck)}
              style={{
                padding: '8px 16px', borderRadius: 4,
                background: 'var(--gold)', color: 'var(--green-deep)',
                border: 'none', fontWeight: 700, fontSize: '0.85rem',
                cursor: (busy || (stubAckRequired && !stubAgeAck)) ? 'not-allowed' : 'pointer',
                opacity: (busy || (stubAckRequired && !stubAgeAck)) ? 0.6 : 1,
              }}
            >Save</button>
          </div>
          {stubAckRequired && (
            <label
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                padding: '8px 10px', borderRadius: 6, marginTop: 10,
                background: 'rgba(212,185,122,0.08)', border: '1px solid var(--gold)',
                fontSize: '0.8rem', cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={stubAgeAck}
                onChange={(e) => setStubAgeAck(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>
                I confirm every participant in this wager is 18 or older.
                <span style={{ display: 'block', color: 'var(--gray-400)', fontSize: '0.7rem', marginTop: 2 }}>
                  Required to convert this bragging-rights game into a money game (TournyFlex never holds funds).
                </span>
              </span>
            </label>
          )}
        </>
      )}
    </div>
  )
}

const miniStyle = {
  padding: '2px 8px', borderRadius: 4,
  background: 'transparent', color: 'var(--cream)',
  border: '1px solid var(--green-mid)',
  fontSize: '0.75rem', cursor: 'pointer',
}

